import { flattenEdges } from "@pathwright/utils"
import { AugmentationPanelItemProps } from "@pathwright/ux/src/components/augmentation/AugmentationPanelSection"
import { useCallback, useMemo, useState } from "react"
import {
  CoverPageCategoryFragmentDoc,
  useCategoriesQuery
} from "@pathwright/graphql-client/src/generated"
import { usePathwrightContext } from "../../pathwright/PathwrightContext"
import { useCoverPageContext } from "../CoverPageProvider"
import { COVER_PAGE_PANEL_PAGE_SIZE } from "../constants"
import {
  COVER_PAGE_CATEGORY_POSITION_ITEM_KEY,
  getCategoryPositioningContext
} from "../positioning"
import { useCoverPageTranslate } from "../utils"
import {
  CoverPagePanelAddProps,
  CoverPagePanelSectionBlock
} from "./CoverPagePanelSection"
import {
  useCoverPagePanelErrorMessage,
  useCoverPagePanelDebouncedValue
} from "./utils"

type CategoryOption = {
  id: number
  name: string
}

export function CoverPagePanelCategoriesSection() {
  const { t } = useCoverPageTranslate()
  const getErrorMessage = useCoverPagePanelErrorMessage()
  const { school } = usePathwrightContext()

  const resource = useCoverPageContext((value) => value.resource)
  const resourceId = resource?.id
  const categories = useCoverPageContext((value) => value.categories)
  const updateCoverPageResourceDetails = useCoverPageContext(
    (value) => value.updateCoverPageResourceDetails
  )
  const [search, setSearch] = useState("")
  const debouncedSearch = useCoverPagePanelDebouncedValue(search)
  const positioningContext = resourceId
    ? getCategoryPositioningContext(resourceId)
    : undefined
  const categoriesQuery = useCategoriesQuery({
    variables: {
      first: COVER_PAGE_PANEL_PAGE_SIZE,
      school_id: school?.id || 0,
      search: debouncedSearch || undefined,
      ids: categories.length
        ? { nin: categories.map((value) => String(value.id)) }
        : undefined,
      manage: true,
      positioningContext
    },
    // NOTE: ideally we skip this query until the selector is engaged.
    skip: !school?.id,
    notifyOnNetworkStatusChange: true,
    // Revalidate when the panel remounts; avoid stale lists after category creation elsewhere.
    fetchPolicy: "cache-and-network",
    nextFetchPolicy: "cache-first"
  })

  const handleAddCategory = useCallback(
    async (categoryId: number) => {
      return updateCoverPageResourceDetails({
        categories: Array.from(
          new Set(categories.map((category) => category.id).concat(categoryId))
        )
      })
    },
    [categories, updateCoverPageResourceDetails]
  )

  const handleRemoveCategory = useCallback(
    async (categoryId: number) => {
      updateCoverPageResourceDetails({
        categories: categories
          .filter((category) => category.id !== categoryId)
          .map((category) => category.id)
      })
    },
    [categories, updateCoverPageResourceDetails]
  )

  const options = useMemo<CategoryOption[]>(
    () =>
      (
        flattenEdges(
          categoriesQuery.data?.context?.school?.categories
        ) as Array<{
          id: number
          name: string
        }>
      )?.map((category) => ({
        id: category.id,
        name: category.name
      })) || [],
    [categoriesQuery.data]
  )

  const handleLoadMore = useCallback(async () => {
    const hasMore =
      !!categoriesQuery.data?.context?.school?.categories?.pageInfo?.hasNextPage
    const endCursor =
      categoriesQuery.data?.context?.school?.categories?.pageInfo?.endCursor

    if (!hasMore || !endCursor) return

    await categoriesQuery.fetchMore({
      variables: {
        cursor: endCursor
      }
    })
  }, [categoriesQuery])

  const items = useMemo<AugmentationPanelItemProps[]>(
    () =>
      categories.map((category) => ({
        data: category,
        itemKey: `edit-category-${category.id}`,
        icon: "tag",
        label: category.name,
        panelRoute: `categories/${category.id}/`,
        controls: [
          {
            type: "edit" as const,
            relativeTo: `categories/${category.id}/`
          },
          {
            type: "delete" as const,
            hashKey: "categories",
            handler: () => handleRemoveCategory(category.id)
          }
        ]
      })),
    [categories, handleRemoveCategory]
  )

  const addProps = useMemo<CoverPagePanelAddProps<CategoryOption>>(
    () => ({
      itemKey: "add-category",
      label: t("panel_sections.add_category", { defaultValue: "Add category" }),
      select: {
        itemLabel: t("panel_sections.category", { defaultValue: "Category" }),
        icon: "tag",
        options,
        isLoading: categoriesQuery.loading,
        getNewOptionData: (inputValue) => ({
          id: -1,
          name: inputValue
        }),
        onInputValueChange: setSearch,
        onMenuScrollToBottom: handleLoadMore,
        getErrorMessage: (error) =>
          getErrorMessage(
            error,
            t("panel_errors.add_category", {
              defaultValue: "Error adding Cover Page's category: "
            })
          ),
        navigateCreate: {
          panelRoute: "categories/new"
        },
        onSubmit: async (item) => {
          if (!item.id || item.id < 0) {
            throw new Error(
              t("panel_categories.select_existing_category", {
                defaultValue: "Select an existing category to add."
              })
            )
          }

          await handleAddCategory(item.id)
        }
      }
    }),
    [
      categoriesQuery.loading,
      getErrorMessage,
      handleAddCategory,
      handleLoadMore,
      options,
      t
    ]
  )

  const sortProps = useMemo(
    () =>
      resourceId && positioningContext
        ? {
            positionedItems: categories,
            itemFragmentDocument: CoverPageCategoryFragmentDoc,
            itemKey: COVER_PAGE_CATEGORY_POSITION_ITEM_KEY,
            positioningContext,
            fragmentVariables: {
              categoryPositioningContext: positioningContext
            }
          }
        : undefined,
    [categories, positioningContext, resourceId]
  )

  return (
    <CoverPagePanelSectionBlock
      heading={t("panel_sections.categories", { defaultValue: "Categories" })}
      headingLinkProps={{ relativeTo: "." }}
      items={items}
      addProps={addProps}
      sortProps={sortProps}
      sectionKey="categories"
    />
  )
}
